import React, { useEffect, useRef, useState } from 'react';
import { X, Loader2, CreditCard, ShieldCheck } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { loadSquareSdk, squareVerifyBuyer } from '@/components/checkout/SquareApplePayButton';

// On-screen Square Web Payments panel for the venue POS. The card fields and
// the Apple Pay / Google Pay button are rendered by Square inside this panel —
// the staff member never leaves /venue/pos, and 3D Secure verification happens
// inline.

const SQUARE_APP_ID = import.meta.env.VITE_SQUARE_APP_ID || 'sq0idp-HblpXluK_lgmGh2wdXzdCA';
const SQUARE_LOCATION_ID = import.meta.env.VITE_SQUARE_LOCATION_ID || 'LHHYN7QJW6P94';

const CARD_STYLE = {
  '.input-container': { borderColor: 'rgba(255,255,255,0.12)', borderRadius: '0px' },
  '.input-container.is-focus': { borderColor: 'rgba(227,29,38,0.7)' },
  input: { color: '#ffffff', backgroundColor: '#0d0d0f', fontSize: '16px' },
  'input::placeholder': { color: 'rgba(255,255,255,0.2)' },
  '.message-text': { color: 'rgba(255,255,255,0.45)' },
};

export default function WalkInSquarePayment({ payload, onPaid, onCancel }) {
  const [ready, setReady] = useState(false);
  const [walletReady, setWalletReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const cardRef = useRef(null);
  const walletRef = useRef(null);
  const busyRef = useRef(false);
  const amount = Number(payload.amount) || 0;
  busyRef.current = busy;

  const billingContact = {
    givenName: payload.firstName || payload.customerName || 'Guest',
    familyName: payload.lastName || '',
    email: payload.customerEmail || '',
    phone: payload.customerPhone || '',
    addressLines: [],
    postalCode: payload.postcode || '',
    countryCode: 'GB',
  };

  const charge = async (sourceId) => {
    setBusy(true);
    setError('');

    // 3D Secure / SCA — done here, in the panel, without navigating away. If the
    // bank asks nothing, Square simply hands back a verification token.
    let verificationToken = null;
    try {
      verificationToken = await squareVerifyBuyer(sourceId, {
        amount: amount.toFixed(2),
        currencyCode: 'GBP',
        intent: 'CHARGE',
        billingContact,
      });
    } catch (e) {
      console.warn('[POS] buyer verification unavailable:', e?.message || e);
    }

    try {
      const res = await base44.functions.invoke('walkInSquareCharge', {
        ...payload,
        sourceId,
        verificationToken,
      });
      if (res.data?.success) {
        onPaid(res.data);
        return;
      }
      setError(res.data?.error || 'Payment was declined. Please try another card.');
    } catch (e) {
      setError(e?.message || 'Payment failed. Please try again.');
    }
    setBusy(false);
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await loadSquareSdk();
        if (cancelled) return;

        const payments = window.Square.payments(SQUARE_APP_ID, SQUARE_LOCATION_ID);

        const card = await payments.card({ style: CARD_STYLE });
        if (cancelled) { try { await card.destroy(); } catch {} return; }
        await card.attach('#pos-sq-card');
        cardRef.current = card;
        setReady(true);

        // Apple Pay on Safari (verified domain), Google Pay everywhere else that
        // supports it. Whichever the device offers fills the same slot.
        const request = payments.paymentRequest({
          countryCode: 'GB',
          currencyCode: 'GBP',
          total: { amount: amount.toFixed(2), label: 'Motion Reality' },
        });

        const attachWallet = async (instance) => {
          if (cancelled) { try { await instance.destroy(); } catch {} return; }
          const container = document.getElementById('pos-sq-wallet');
          if (!container) { try { await instance.destroy(); } catch {} return; }
          container.innerHTML = '';
          await instance.attach('#pos-sq-wallet');
          // tokenize() must be the first async call in the click handler.
          container.onclick = async () => {
            if (busyRef.current) return;
            try {
              const result = await instance.tokenize();
              if (result.status === 'OK') charge(result.token);
            } catch {
              // Wallet sheet dismissed by the customer — nothing to report.
            }
          };
          walletRef.current = instance;
          setWalletReady(true);
        };

        try {
          attachWallet(await payments.applePay(request));
        } catch {
          try {
            attachWallet(await payments.googlePay(request));
          } catch {
            // No wallet on this device — card entry is the only option.
          }
        }
      } catch (e) {
        if (!cancelled) {
          console.error('[POS] Square SDK init failed:', e?.message || e);
          setError('Could not load the card terminal. Close this panel and try again.');
        }
      }
    })();

    return () => {
      cancelled = true;
      try { cardRef.current?.destroy(); } catch {}
      try { walletRef.current?.destroy(); } catch {}
      cardRef.current = null;
      walletRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCardPay = async () => {
    if (!cardRef.current || busy) return;
    setError('');
    const result = await cardRef.current.tokenize();
    if (result.status !== 'OK') {
      setError(result.errors?.[0]?.message || 'Please check the card details.');
      return;
    }
    await charge(result.token);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="bg-[#111115] border border-white/10 w-full max-w-lg my-auto">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-white/10">
          <div>
            <p className="text-primary text-[10px] font-bold tracking-widest">PAY ON THIS iPAD</p>
            <p className="text-white font-black text-3xl mt-1">£{amount.toFixed(2)}</p>
            <p className="text-white/40 text-xs mt-1">{payload.experienceName || payload.experienceType}</p>
          </div>
          <button
            onClick={onCancel}
            disabled={busy}
            className="text-white/40 hover:text-white transition-colors disabled:opacity-30"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <p className="text-white/40 text-[10px] font-bold tracking-widest uppercase mb-2">Express checkout</p>
            <div
              id="pos-sq-wallet"
              className={`min-h-[48px] [&_button]:w-full ${walletReady ? '' : 'hidden'}`}
            />
            {!walletReady && (
              <p className="text-white/25 text-xs border border-dashed border-white/10 px-3 py-3">
                Apple Pay / Google Pay is not available on this device — pay by card below.
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <span className="flex-1 h-px bg-white/10" />
            <span className="text-white/30 text-[10px] font-bold tracking-widest">OR PAY BY CARD</span>
            <span className="flex-1 h-px bg-white/10" />
          </div>

          <div>
            <p className="text-white/40 text-[10px] font-bold tracking-widest uppercase mb-2">Card details</p>
            <div id="pos-sq-card" className="min-h-[48px]" />
            {!ready && !error && (
              <p className="flex items-center gap-2 text-white/40 text-xs mt-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading the card terminal…
              </p>
            )}
          </div>

          {error && (
            <div className="border border-red-500/40 bg-red-500/10 px-3 py-2.5">
              <p className="text-red-400 text-sm font-bold">{error}</p>
              <p className="text-red-400/60 text-xs mt-1">Adjust the card and try again — nothing has been charged.</p>
            </div>
          )}

          <button
            onClick={handleCardPay}
            disabled={!ready || busy}
            className="w-full bg-primary hover:bg-primary/90 disabled:bg-white/5 disabled:text-white/25 text-white font-black text-sm tracking-widest py-5 flex items-center justify-center gap-2 transition-colors"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            {busy ? 'PROCESSING…' : `PAY £${amount.toFixed(2)}`}
          </button>

          <p className="flex items-center justify-center gap-1.5 text-white/30 text-[10px] font-bold tracking-widest">
            <ShieldCheck className="w-3.5 h-3.5" /> SECURED BY SQUARE · 3D SECURE SUPPORTED
          </p>
        </div>
      </div>
    </div>
  );
}